import React, { useState } from 'react';
import { intl } from '../../lib';
import type { ChildrenProp, ClassNameProp } from '../common-types';

/**
 * Props for a {@link Details} component.
 */
export type DetailsProps = ClassNameProp & Required<ChildrenProp>;

/**
 * Component for displaying details in a simple manner.
 * This component is essentially a replacement for the `<details>` HTML tag, since that tag
 * is not supported properly in IE.
 *
 * The component is kept to be as simple as it could possibly be, to prevent it from being able to throw any exceptions
 * since this is used in the ErrorBoundary.
 *
 * @param children component children
 * @param className css class
 * @param props leftover props
 * @return the created {@link React.JSX.Element}
 * @constructor
 */
export function Details({
  children,
  className = '',
  ...props
}: Readonly<DetailsProps>): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      {...props}
      className={`ehs-details ${className}`.trim()}
      role="region"
      aria-expanded={isOpen}
    >
      <div
        className="ehs-details-head"
        onClick={(): void => setIsOpen(!isOpen)}
      >
        {/* see https://unicode-table.com for codes */}
        <span className="arrow">{isOpen ? '\u25be' : '\u25b8'}</span>
        &nbsp;{intl('common.details')}
      </div>
      <div
        className="ehs-details-body"
        style={{
          display: isOpen ? 'block' : 'none'
        }}
      >
        {children}
      </div>
    </div>
  );
}
